import './TopBar.scss';

import {
  Button,
  Divider,
  Dropdown,
  DropdownItem,
  Icon,
  Masthead,
  MastheadBrand,
  MastheadContent,
  MastheadLogo,
  MastheadMain,
  MastheadProps,
  MastheadToggle,
  MenuToggle,
  MenuToggleElement,
  ToolbarItem,
} from '@patternfly/react-core';
import { EllipsisVIcon, ExternalLinkAltIcon, FireIcon, GithubIcon } from '@patternfly/react-icons';
import { BarsIcon } from '@patternfly/react-icons/dist/js/icons/bars-icon';
import React, { FunctionComponent, useCallback } from 'react';
import { Link } from 'react-router';

import camelLogo from '../assets/camel-logo.svg';
import logo from '../assets/logo-kaoto.png';
import { useComponentLink } from '../hooks/ComponentLink';
import { DEFAULT_POPPER_PROPS } from '../models/popper-default';
import { Links } from '../router/links.models';
import { KaotoAboutModal } from './KaotoAboutModal';

interface ITopBar {
  navToggle: () => void;
}

const displayObject: MastheadProps['display'] = {
  default: 'inline',
};

const TopBarMenuToggle: FunctionComponent<{
  toggleRef: React.Ref<MenuToggleElement>;
  onClick: () => void;
  isOpen: boolean;
}> = ({ toggleRef, onClick, isOpen }) => (
  <MenuToggle ref={toggleRef} onClick={onClick} variant="plain" isExpanded={isOpen}>
    <EllipsisVIcon />
  </MenuToggle>
);

export const TopBar: FunctionComponent<ITopBar> = (props) => {
  const logoLink = useComponentLink(Links.Home);
  const [isOpen, setIsOpen] = React.useState(false);
  const [isAboutModalOpen, setIsAboutModalOpen] = React.useState(false);

  const onToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const onSelect = (event: React.MouseEvent<Element, MouseEvent> | undefined) => {
    event?.stopPropagation();
    setIsOpen(!isOpen);
  };

  const toggleAboutModal = () => {
    setIsAboutModalOpen(!isAboutModalOpen);
  };

  const renderToggle = useCallback(
    (toggleRef: React.Ref<MenuToggleElement>) => (
      <TopBarMenuToggle toggleRef={toggleRef} onClick={onToggle} isOpen={isOpen} />
    ),
    [onToggle, isOpen],
  );

  return (
    <>
      <Masthead id="stack-inline-masthead" display={displayObject}>
        <MastheadMain>
          <MastheadToggle>
            <Button icon={<BarsIcon />} variant="plain" onClick={props.navToggle} aria-label="Global navigation" />
          </MastheadToggle>
          <MastheadBrand data-codemods>
            <MastheadLogo data-codemods component={logoLink} className="masthead-logo">
              <img className="shell__logo" src={logo} alt="Kaoto Logo" />
            </MastheadLogo>
          </MastheadBrand>
        </MastheadMain>

        <MastheadContent>
          <ToolbarItem className="shell__link">
            <Dropdown
              onSelect={onSelect}
              toggle={renderToggle}
              isOpen={isOpen}
              onOpenChange={(isOpen: boolean) => {
                setIsOpen(isOpen);
              }}
              popperProps={DEFAULT_POPPER_PROPS}
            >
              <a href="https://kaoto.io/workshop/" target="_blank" rel="noopener noreferrer">
                <DropdownItem key="tutorial">
                  <Icon isInline>
                    <ExternalLinkAltIcon />
                  </Icon>
                  &nbsp;<span className="pf-u-mr-lg">Tutorials</span>
                </DropdownItem>
              </a>
              <a href="https://kaoto.io/docs/" target="_blank" rel="noopener noreferrer">
                <DropdownItem key="help">
                  <Icon isInline>
                    <ExternalLinkAltIcon />
                  </Icon>
                  &nbsp;<span className="pf-u-mr-lg">Help</span>
                </DropdownItem>
              </a>
              <a href="https://github.com/KaotoIO/kaoto-examples" target="_blank" rel="noopener noreferrer">
                <DropdownItem key="feedback">
                  <Icon isInline>
                    <GithubIcon />
                  </Icon>
                  &nbsp;<span className="pf-u-mr-lg">Examples</span>
                </DropdownItem>
              </a>
              <a href="https://github.com/KaotoIO/kaoto/issues/new/choose" target="_blank" rel="noopener noreferrer">
                <DropdownItem key="feedback">
                  <Icon isInline>
                    <GithubIcon />
                  </Icon>
                  &nbsp;<span className="pf-u-mr-lg">Feedback</span>
                </DropdownItem>
              </a>

              <Divider component="li" key="separator1" />
              <a
                href="https://camel.apache.org/camel-core/getting-started/index.html"
                target="_blank"
                rel="noopener noreferrer"
              >
                <DropdownItem key="camel">
                  <Icon isInline>
                    <img src={camelLogo} alt="Camel logo" />
                  </Icon>
                  &nbsp;<span className="pf-u-mr-lg">Apache Camel</span>
                </DropdownItem>
              </a>
              <a href="https://hawt.io/docs/get-started.html" target="_blank" rel="noopener noreferrer">
                <DropdownItem key="hawtio">
                  <Icon isInline>
                    <FireIcon />
                  </Icon>
                  &nbsp;<span className="pf-u-mr-lg">Hawtio</span>
                </DropdownItem>
              </a>

              <Divider component="li" key="separator2" />
              <Link data-testid="settings-link" to={Links.Settings}>
                <DropdownItem id="settings" key="settings">
                  Settings
                </DropdownItem>
              </Link>
              <Divider component="li" key="separator3" />
              <DropdownItem
                id="about"
                key="about"
                onClick={() => {
                  toggleAboutModal();
                }}
              >
                About
              </DropdownItem>
            </Dropdown>
          </ToolbarItem>
        </MastheadContent>
      </Masthead>

      <KaotoAboutModal
        handleCloseModal={() => {
          toggleAboutModal();
        }}
        isModalOpen={isAboutModalOpen}
      />
    </>
  );
};
